import React, { useEffect, useMemo } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useUser } from '../../../hooks/useUser';
import sidebarMenu from './configuration/SidebarMenu';
import styles from './settings.module.css';
import { setPageTitle } from '../../../components/PageTitle';
import { isInternal } from '../../../utils';
import { MainContainer } from '../../../components/ContentWrapper';
import { usePortalPath } from '../../../hooks/usePortalPath';

export default function SettingsPage() {
  const { user, role } = useUser();
  const { p } = usePortalPath();

  // The tabs span two mounts (TDC-1609): personal tabs at /settings/*, brand
  // tabs under the brand's portal. Both hrefs are built ABSOLUTELY — a
  // relative <NavLink to="team"> would resolve against whichever mount is
  // currently active and cross into the wrong space.
  const hrefFor = (item) =>
    item.scope === 'brand' ? p(`/settings/${item.link}`) : `/settings/${item.link}`;

  // prevent normal team member to access to team settings page
  const menus = useMemo(() => {
    // Brand sales registers profile + reset-password + onboarding routes
    // (router/brandSales.tsx) — the menu must match.
    if (role === 'BRAND_TEAM_SALES') {
      return sidebarMenu.filter((item) =>
        ['profile', 'reset-password', 'sign-in-methods', 'onboarding'].includes(item.link)
      );
    }

    if (role === 'BRAND_MEMBER') {
      return sidebarMenu.filter(
        (item) =>
          item.link !== 'team' &&
          item.link !== 'white-label' &&
          item.link !== 'personal-keys' &&
          item.label !== 'Brand' &&
          item.label !== 'Subscription'
      );
    }

    // Internal staff outside a brand workspace have no brand: the brand
    // tabs (onboarding, API keys, team, brand profile, white label) live on the brand
    // detail page (/brands/:id) instead. Sign-in methods stays: staff can
    // use Google too.
    if (isInternal(role)) {
      return sidebarMenu.filter(
        (item) =>
          item.link !== 'onboarding' &&
          item.link !== 'search-console' &&
          item.link !== 'api-keys' &&
          item.link !== 'white-label' &&
          item.link !== 'team' &&
          item.label !== 'Brand' &&
          item.label !== 'Subscription'
      );
    }

    // Personal API Keys are internal-staff only for now.
    return sidebarMenu.filter((item) => item.link !== 'personal-keys');
  }, [role]);

  useEffect(() => {
    setPageTitle('My profile');
  }, []);

  return (
    <MainContainer title="Settings">
      <div className="flex flex-col md:flex-row md:justify-between items-stretch md:items-start gap-4 md:gap-6">
        <div className="bg-surface rounded-xl py-2 md:py-4 w-full md:w-[260px] lg:w-[300px] md:shrink-0 overflow-x-auto md:overflow-visible">
          <div className="flex md:flex-col flex-row gap-1 md:gap-0 px-2 md:px-0 min-w-max md:min-w-0">
            {menus.map((sidebarItem, index) => (
              <NavLink
                key={index}
                to={hrefFor(sidebarItem)}
                className={({ isActive }) =>
                  `${styles.navlink} ${isActive && styles.active}`
                }
                exact={sidebarItem.exact ? 'true' : 'false'}
              >
                {sidebarItem.label}
              </NavLink>
            ))}
          </div>
        </div>
        <div className="bg-surface rounded-xl p-4 sm:p-5 w-full min-w-0">
          <Outlet />
        </div>
      </div>
    </MainContainer>
  );
}
